import { useState, useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { Header } from '../components/Header';
import { PspAnnouncements } from '../components/Announcements/PspAnnouncements';
import { PspGuides } from '../components/Guides/PspGuides';
import { NavigationSidebar } from '../components/NavigationSidebar';
import { SkipLink } from '../components/SkipLink';
import { TestIds, SHELL_CONTENT_OFFSET_PX } from '../constants';
import { usePspContext } from '../contexts/PspContext';
import { DrawerSlotRegion } from '../components/DrawerSlots/DrawerSlotRegion';
import { OverlayDrawerHost } from '../components/OverlayDrawer/OverlayDrawerHost';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePageRefreshOnNavigation } from '../hooks/usePageRefreshOnNavigation';
import { useSuppressGlobalNavUi } from '../hooks/useSuppressGlobalNavUi';
import { getAllRefreshBasePaths } from '../services/pageRefreshUtils';
import { GlobalApps } from '../components/GlobalApps/GlobalApps';
import { useNavigationBroadcast } from '../hooks/useNavigationBroadcast';
import { useDrawerSlotBroadcast } from '../hooks/useDrawerSlotBroadcast';
import { useOverlaySlotBroadcast } from '../hooks/useOverlaySlotBroadcast';

function LayoutContent({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" sx={{ flex: 1, overflow: 'hidden' }}>
      <Box
        component="main"
        role="main"
        id="psp-main-content"
        aria-label="Main content"
        data-testid={TestIds.MAIN_CONTENT}
        tabIndex={-1}
        sx={{
          flex: 1,
          // Allow <main> to shrink below its content's intrinsic width so the
          // drawer slots beside it can claim space without overflowing.
          minWidth: 0,
          position: 'relative',
          height: `calc(100vh - ${SHELL_CONTENT_OFFSET_PX}px)`,
          overflow: 'auto',
          '&:focus': {
            outline: 'none',
          },
        }}
      >
        {children}
      </Box>
      <DrawerSlotRegion />
    </Stack>
  );
}

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  // Suppresses remote app nav bar OG logo, user profile, and context switcher dropdown
  const suppressGlobalNav = useSuppressGlobalNavUi();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const isFirstRender = useRef(true);
  const layoutRef = useRef<HTMLDivElement>(null);
  const pspContext = usePspContext();
  const pageTitle = usePageTitle(location.pathname, pspContext?.appRegistry);
  useNavigationBroadcast();
  // Emit `shellDrawerOpen` so embedded modules can close their own right-side
  // panels when a shell drawer (e.g. OG Assist) opens.
  useDrawerSlotBroadcast();
  // Emit `shellOverlayOpen` / `shellOverlayClose` for the shared overlay host.
  useOverlaySlotBroadcast();

  // Cross-app nav refresh: PspContext projects PSP_UI_LSF_PAGE_REFRESH onto every
  // registry entry, so when the flag is off this resolves to an empty array.
  const refreshBasePaths = getAllRefreshBasePaths(
    pspContext?.appRegistry,
    pspContext?.routeRegistry
  );

  usePageRefreshOnNavigation(refreshBasePaths);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Reset focus on route change so skip link is first Tab target
  useEffect(() => {
    // Skip on initial mount to avoid interfering with page load
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Focus layout container - next Tab will go to skip link
    layoutRef.current?.focus();
  }, [location.pathname]);

  // Update document title when page title changes
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <>
      {suppressGlobalNav}
      <Box
        ref={layoutRef}
        tabIndex={-1}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          '&:focus': {
            outline: 'none',
          },
        }}
      >
        <SkipLink />

        <Header onMenuClick={toggleSidebar} />

        {/* Overlay, by design: a zero-height anchor here, with the banners drawn
            over the top of <main>. It takes no column height, so the shell's
            65 px offset, Command Center and mounted modules are unchanged, and
            the page never shifts when a banner appears or is closed. While a
            banner is open it covers the top of the page, so it must be
            closable: status-page incidents always are (SDK 0.7.2+), and a
            LaunchDarkly banner unless its flag sets `dismissible: false`.
            Before #psp-main-content in tab
            order, which is right for urgent messages. Renders nothing when
            there is nothing to show, or when it fails (its own error boundary). */}
        <PspAnnouncements />
        {/* Guide Studio guides for every page; renders nothing in the tree. */}
        <PspGuides />

        <NavigationSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <LayoutContent>{children}</LayoutContent>
        <OverlayDrawerHost />
        <GlobalApps />
      </Box>
    </>
  );
}
